import React, { useState, useEffect, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FaSearch, FaEllipsisV, FaEye, FaEdit, FaSync, FaFilter } from 'react-icons/fa';
import AppContext from '../../../context/AppContext';
import axios from 'axios';

const PackageList = () => {
    const { openDetailsModal, openEditModal, refreshPackages } = useOutletContext();
    const { baseUrl, authToken, imageBaseUrl } = useContext(AppContext);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionMenuOpen, setActionMenuOpen] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        category: 'all',
        sortBy: 'newest'
    });

    const fetchPackages = () => {
        setLoading(true);
        setError(null);

        axios.get(`${baseUrl}/admin/travel-packages-get`, {
            headers: { "Authorization": `Bearer ${authToken}` }
        })
        .then((response) => {
            if (response.data.success) {
                setPackages(response.data.data);
            } else {
                setError('Failed to load packages');
            }

            setLoading(false);
        })
        .catch((error) => {
            console.error('Fetch error:', error);
            setError('Unable to connect to server');
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchPackages();
    }, [refreshPackages]);

    const getFilteredPackages = () => {
        let filtered = [...packages];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filtered = filtered.filter(p =>
                p.package_name?.toLowerCase().includes(searchLower) ||
                p.location?.toLowerCase().includes(searchLower) ||
                p.package_destination?.toLowerCase().includes(searchLower)
            );
        }

        if (filters.status !== 'all') {
            if (filters.status === 'available') {
                filtered = filtered.filter(p => p.is_available === true || p.is_available === 1);
            } else if (filters.status === 'unavailable') {
                filtered = filtered.filter(p => p.is_available === false || p.is_available === 0);
            } else {
                filtered = filtered.filter(p => p.package_status === filters.status);
            }
        }

        if (filters.category !== 'all') {
            filtered = filtered.filter(p => p.package_category === filters.category);
        }

        switch (filters.sortBy) {
            case 'newest':
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'oldest':
                filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                break;
            case 'price-low':
                filtered.sort((a, b) => (a.package_price || 0) - (b.package_price || 0));
                break;
            case 'price-high':
                filtered.sort((a, b) => (b.package_price || 0) - (a.package_price || 0));
                break;
            default:
                break;
        }
        return filtered;
    };

    const getStatusClass = (pkg) => {
        if (pkg.package_status === 'archived') return 'adm-pkg-status-archived-badge';
        if (pkg.package_status === 'published') return 'adm-pkg-status-published';
        return 'adm-pkg-status-draft';
    };

    const getStatusLabel = (pkg) => {
        const map = { draft: 'Draft', published: 'Published', archived: 'Archived' };
        return map[pkg.package_status] || pkg.package_status;
    };

    const resetFilters = () => {
        setFilters({ search: '', status: 'all', category: 'all', sortBy: 'newest' });
    };

    const uniqueCategories = [...new Set(packages.map(p => p.package_category).filter(Boolean))];
    const filteredPackages = getFilteredPackages();

    if (loading) {
        return (
            <div className="adm-pkg-loading-state">
                <div className="adm-pkg-spinner"></div>
                <p>Loading packages...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="adm-pkg-error-state">
                <p>{error}</p>
                <button className="adm-pkg-btn-retry" onClick={fetchPackages}>
                    <FaSync /> Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="adm-pkg-list-container">
            <div className="adm-pkg-filters-bar">
                <div className="adm-pkg-search-box">
                    <FaSearch className="adm-pkg-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, destination, or location..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="adm-pkg-search-input"
                    />
                </div>

                <div className="adm-pkg-filter-group">
                    <div className="adm-pkg-filter-item">
                        <label className="adm-pkg-filter-label"><FaFilter /> Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="adm-pkg-filter-select"
                        >
                            <option value="all">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                        </select>
                    </div>

                    <div className="adm-pkg-filter-item">
                        <label className="adm-pkg-filter-label"><FaFilter /> Category</label>
                        <select
                            value={filters.category}
                            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                            className="adm-pkg-filter-select"
                        >
                            <option value="all">All Categories</option>
                            {uniqueCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    <div className="adm-pkg-filter-item">
                        <label className="adm-pkg-filter-label"><FaFilter /> Sort By</label>
                        <select
                            value={filters.sortBy}
                            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                            className="adm-pkg-filter-select"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                        </select>
                    </div>

                    <button className="adm-pkg-filter-reset-btn" onClick={resetFilters}>
                        Reset Filters
                    </button>

                    <button className="adm-pkg-btn-refresh" onClick={fetchPackages} title="Refresh">
                        <FaSync />
                    </button>
                </div>
            </div>

            <div className="adm-pkg-results-bar">
                Showing <strong>{filteredPackages.length}</strong> of <strong>{packages.length}</strong> packages
            </div>

            <div className="adm-pkg-grid">
                {filteredPackages.length === 0 ? (
                    <div className="adm-pkg-empty">
                        <p>No packages found.</p>
                    </div>
                ) : (
                    filteredPackages.map(pkg => (
                        <div key={pkg.id} className="adm-pkg-card">
                            <div className="adm-pkg-card-image">
                                <img
                                    src={
    pkg.package_image
        ? `${imageBaseUrl}/${pkg.package_image.replace(/^\/?storage\//, "")}`
        : "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600"
}
                                    alt={pkg.package_name}
                                    onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600';
                                    }}
                                />

                                <div className={`adm-pkg-status-badge ${getStatusClass(pkg)}`}>
                                    {getStatusLabel(pkg)}
                                </div>

                                <div
                                    className={`adm-pkg-availability-dot ${pkg.is_available ? 'adm-pkg-dot-available' : 'adm-pkg-dot-unavailable'}`}
                                    title={pkg.is_available ? 'Available' : 'Not Available'}
                                />
                            </div>

                            <div className="adm-pkg-card-content">
                                <div className="adm-pkg-card-title-row">
                                    <h3 className="adm-pkg-card-title">{pkg.package_name}</h3>
                                    <div className="adm-pkg-card-actions">
                                        <button
                                            className="adm-pkg-action-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActionMenuOpen(actionMenuOpen === pkg.id ? null : pkg.id);
                                            }}
                                        >
                                            <FaEllipsisV />
                                        </button>
                                        {actionMenuOpen === pkg.id && (
                                            <div className="adm-pkg-action-dropdown">
                                                <button onClick={() => { openDetailsModal(pkg); setActionMenuOpen(null); }}>
                                                    <FaEye /> View Details
                                                </button>
                                                <button onClick={() => { openEditModal(pkg); setActionMenuOpen(null); }}>
                                                    <FaEdit /> Edit Package
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="adm-pkg-card-meta">
                                    <span className="adm-pkg-category-tag">{pkg.package_category || 'Uncategorized'}</span>
                                </div>

                                <div className="adm-pkg-card-details">
                                    {pkg.package_destination && (
                                        <div className="adm-pkg-detail">
                                            <span className="adm-pkg-detail-label">Destination</span>
                                            <span className="adm-pkg-detail-value">{pkg.package_destination}</span>
                                        </div>
                                    )}
                                    {pkg.location && (
                                        <div className="adm-pkg-detail">
                                            <span className="adm-pkg-detail-label">Location</span>
                                            <span className="adm-pkg-detail-value">{pkg.location}</span>
                                        </div>
                                    )}
                                    {(pkg.duration_days || pkg.duration_nights) && (
                                        <div className="adm-pkg-detail">
                                            <span className="adm-pkg-detail-label">Duration</span>
                                            <span className="adm-pkg-detail-value">{pkg.duration_days || 0}D / {pkg.duration_nights || 0}N</span>
                                        </div>
                                    )}
                                    {pkg.max_people_size && (
                                        <div className="adm-pkg-detail">
                                            <span className="adm-pkg-detail-label">Group Size</span>
                                            <span className="adm-pkg-detail-value">Max {pkg.max_people_size} persons</span>
                                        </div>
                                    )}
                                </div>

                                <div className="adm-pkg-card-footer">
                                    <div className="adm-pkg-price">
                                        <span className="adm-pkg-price-label">Price</span>
                                        <span className="adm-pkg-price-value">PKR {Number(pkg.package_price)?.toLocaleString() || '0'}</span>
                                    </div>
                                    <div className={`adm-pkg-avail-badge ${pkg.is_available ? 'adm-pkg-avail-yes' : 'adm-pkg-avail-no'}`}>
                                        {pkg.is_available ? 'Available' : 'Unavailable'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PackageList;